import React, { useEffect, useState } from 'react';
import { getScratches, setGift } from '../../api/campaign';
import { Dropdown, DropdownItem, Table, TableBody, TableCell, TableHead, TableHeadCell, TableRow, Modal, ModalBody, ModalHeader, Spinner, Button } from "flowbite-react";
import img from '../../scratch_foreground.jpg';

export default function Dash() {
    const [details, setDetails] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [openModal, setOpenModal] = useState(false);
    const [filteredData, setFilteredData] = useState([]);
    const [imageUrl, setImageUrl] = useState('');
    
    // Separate state for each filter
    const [selectedDate, setSelectedDate] = useState('');
    const [selectedBrand, setSelectedBrand] = useState('');
    const [selectedStatus, setSelectedStatus] = useState('');
    const [selectedGift, setSelectedGift] = useState('');

    // Combined filter function - applies ALL filters together
    const applyAllFilters = () => {
        let filtered = [...details];

        // 1. Apply Date Filter
        if (selectedDate) {
            filtered = filtered.filter(item => {
                const itemDate = new Date(item.dt).toISOString().split('T')[0];
                return itemDate === selectedDate;
            });
        }

        // 2. Apply Brand Filter
        if (selectedBrand === 'inv') {
            filtered = filtered.filter(item => item.innovate?.toLowerCase().includes('x'));
        } else if (selectedBrand === 'atlas') {
            filtered = filtered.filter(item => item.atlas?.toLowerCase().includes('x'));
        } else if (selectedBrand === 'all') {
            filtered = filtered.filter(item =>
                item.innovate?.toLowerCase().includes('x') ||
                item.atlas?.toLowerCase().includes('x')
            );
        }
        // Empty string means no brand filter

        // 3. Apply Win/Lost Status Filter
        if (selectedStatus === 'win') {
            filtered = filtered.filter(item => item.s === 1);
        } else if (selectedStatus === 'lost') {
            filtered = filtered.filter(item => item.s === 0);
        }
        // Empty string means no status filter

        // 4. Apply Gift Status Filter
        if (selectedGift === 'g_pen') {
            filtered = filtered.filter(item => 
                item.atlas?.toLowerCase().includes('x') && item.r === 0 && item.s === 1
            );
        } else if (selectedGift === 'g_done') {
            filtered = filtered.filter(item => 
                item.atlas?.toLowerCase().includes('x') && item.r === 1 && item.s === 1
            );
        }
        // Empty string means no gift filter

        setFilteredData(filtered);
    };

    // Re-apply filters whenever any filter changes
    useEffect(() => {
        applyAllFilters();
    }, [details, selectedDate, selectedBrand, selectedStatus, selectedGift]);

    useEffect(() => {
        getDetails();
    }, []);

    // bill_img is now a direct URL in the file store, so there is nothing to fetch
    const getImage = (billImg) => setImageUrl(billImg || '');

    const getDetails = async () => {
        setIsLoading(true);
        setLoadError('');
        try {
            setDetails(await getScratches());
        } catch (err) {
            console.error("error fetching data: ", err);
            setLoadError('Could not load data. Tap retry.');
        } finally {
            setIsLoading(false);
        }
    };

    const giftManage = async (id) => {
        try {
            await setGift(id, 1);
            setDetails(prevDetails =>
                prevDetails.map(item =>
                    item.id === id ? { ...item, r: 1 } : item
                )
            );
        } catch (err) {
            console.error("error fetching data: ", err);
        }
    };

    const clearAllFilters = () => {
        setSelectedDate('');
        setSelectedBrand('');
        setSelectedStatus('');
        setSelectedGift('');
    };

    // Helper function to get brand label
    const getBrandLabel = () => {
        if (!selectedBrand) return 'All Brands';
        if (selectedBrand === 'all') return 'All Brands';
        if (selectedBrand === 'inv') return 'Innovate';
        if (selectedBrand === 'atlas') return 'Atlas';
        return 'Select Brand';
    };

    // Helper function to get status label
    const getStatusLabel = () => {
        if (!selectedStatus) return 'All Status';
        if (selectedStatus === 'win') return 'Win';
        if (selectedStatus === 'lost') return 'Lost';
        return 'Select Status';
    };

    // Helper function to get gift label
    const getGiftLabel = () => {
        if (!selectedGift) return 'All';
        if (selectedGift === 'g_pen') return 'Pending';
        if (selectedGift === 'g_done') return 'Done';
        return 'Select Gift';
    };

    return (
        <>
            <div className='flex flex-col text-center'>
                <h1 className='py-4 text-3xl font-bold'>Gift View</h1>

                <div className='px-8 py-4'>
                    <div className='flex gap-4 items-end flex-wrap'>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                                Filter by Date
                            </label>
                            <input
                                type="date"
                                value={selectedDate}
                                onChange={(e) => setSelectedDate(e.target.value)}
                                className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>
                        
                        <Dropdown label={`Brand: ${getBrandLabel()}`}>
                            <DropdownItem onClick={() => setSelectedBrand('')}>All Brands</DropdownItem>
                            {/* 'all' (Atlas + Innovate) is redundant while only Atlas runs.
                            <DropdownItem onClick={() => setSelectedBrand('all')}>All (Atlas + Innovate)</DropdownItem>
                            */}
                            {/* CAMPAIGN 2 IS ATLAS ONLY -- Innovate filter switched off.
                            <DropdownItem onClick={() => setSelectedBrand('inv')}>Innovate</DropdownItem>
                            */}
                            <DropdownItem onClick={() => setSelectedBrand('atlas')}>Atlas</DropdownItem>
                        </Dropdown>
                        
                        <Dropdown label={`Status: ${getStatusLabel()}`}>
                            <DropdownItem onClick={() => setSelectedStatus('')}>All Status</DropdownItem>
                            <DropdownItem onClick={() => setSelectedStatus('win')}>Win</DropdownItem>
                            <DropdownItem onClick={() => setSelectedStatus('lost')}>Lost</DropdownItem>
                        </Dropdown>
                        
                        
                        {(selectedDate || selectedBrand || selectedStatus || selectedGift) && (
                            <Button onClick={clearAllFilters} color="gray">
                                Clear All Filters
                            </Button>
                        )}
                    </div>
                </div>

                <div className="mb-4 px-8 text-sm text-gray-600 text-left">
                    Showing {filteredData.length} of {details.length} records
                </div>

                {isLoading ? (
                    <div className="py-16 text-center">
                        <Spinner size="xl" />
                        <p className="mt-3 text-sm text-gray-500">Loading data&hellip;</p>
                    </div>
                ) : loadError ? (
                    <div className="py-16 text-center">
                        <p className="mb-3 text-red-600">{loadError}</p>
                        <Button onClick={getDetails}>Retry</Button>
                    </div>
                ) : (
                <div className="overflow-x-auto px-8">
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableHeadCell></TableHeadCell>
                                <TableHeadCell>Name</TableHeadCell>
                                <TableHeadCell>Tp</TableHeadCell>
                                <TableHeadCell>Shop Name</TableHeadCell>
                                <TableHeadCell>Win</TableHeadCell>
                                <TableHeadCell><span className="sr-only">Bill</span></TableHeadCell>
                                <TableHeadCell>Action</TableHeadCell>
                            </TableRow>
                        </TableHead>
                        <TableBody className="divide-y">
                            {filteredData.map((data, index) => (
                                <TableRow
                                    key={index}
                                    className={`${
                                        data.a === 2
                                            ? 'bg-red-100 dark:bg-red-900'
                                            : 'bg-white dark:border-gray-700 dark:bg-gray-800'
                                    }`}
                                >
                                    <TableCell>{index + 1}</TableCell>
                                    <TableCell className={`whitespace-nowrap font-medium ${
                                        data.a === 2 ? 'text-red-900 dark:text-red-100' : 'text-gray-900 dark:text-white'
                                    }`}>
                                        {data.name}
                                    </TableCell>
                                    <TableCell className={data.a === 2 ? 'text-red-900 dark:text-red-100' : ''}>
                                        {data.tp}
                                    </TableCell>
                                    <TableCell className={data.a === 2 ? 'text-red-900 dark:text-red-100' : ''}>
                                        {data.shopName}
                                    </TableCell>
                                    <TableCell className={data.a === 2 ? 'text-red-900 dark:text-red-100' : ''}>
                                        {data.s === 0 ? 'Lost' : 'Win'}
                                    </TableCell>
                                    <TableCell>
                                        <a 
                                            href="#" 
                                            onClick={() => { setOpenModal(true); getImage(data.bill_img); }}
                                            className={`font-medium hover:underline ${
                                                data.a === 2 
                                                    ? 'text-red-700 dark:text-red-300' 
                                                    : 'text-primary-600 dark:text-primary-500'
                                            }`}
                                        >
                                            Bill View
                                        </a>
                                    </TableCell>
                                    <TableCell>
                                        {data.r === 0 && data.s === 1 && data.atlas?.toLowerCase().includes('x') && data.a === 1 && (
                                            <Button onClick={() => giftManage(data.id)}>Gift Received</Button>
                                        )}
                                        {data.a === 2 && (
                                            <span className="text-red-700 dark:text-red-300 font-semibold">Declined</span>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
                )}
            </div>
            <>
                <Modal show={openModal} onClose={() => { setOpenModal(false); setImageUrl(null); }}>
                    <ModalHeader>Bill Image</ModalHeader>
                    <ModalBody>
                        {imageUrl ? (
                            <div className='flex justify-center items-center'>
                                <img src={imageUrl} alt="Bill" className="max-h-[70vh] w-auto object-contain" />
                            </div>
                        ) : (
                            <div className="text-center">
                                <Spinner aria-label="Center-aligned Large spinner example" />
                            </div>
                        )}
                    </ModalBody>
                </Modal>
            </>
        </>
    );
}